import dayjs from 'dayjs';

import { EFlightCategory } from '#shared';

import type { Dayjs } from 'dayjs';

/**
 * Thrown when a raw TAF string does not match the expected grammar. Distinct
 * from unexpected runtime errors (e.g. dayjs/timezone failures) so callers can
 * treat an unrecognised TAF format as a recoverable condition while letting
 * genuine bugs surface.
 */
export class TafFormatError extends Error {
  constructor(rawData: string) {
    super(`Invalid TAF format: ${rawData}`);
    this.name = 'TafFormatError';
  }
}

export const STATION_ID_PATTERN = '[A-Z]{4}';
export enum ETafReportType {
  TAF = 'TAF',
  'TAF AMD' = 'TAF AMD',
  'TAF COR' = 'TAF COR',
}

export enum ESkyCoverContraction {
  SKC = 'SKC',
  FEW = 'FEW',
  SCT = 'SCT',
  BKN = 'BKN',
  OVC = 'OVC',
}
export const REPORT_TYPE_PATTERN = `(${ETafReportType.TAF})|(${ETafReportType['TAF AMD']})|(${ETafReportType['TAF COR']})`;
export const DATE_TIME_GROUP_PATTERN = '\\d{6}Z';
export const VALID_PERIOD_PATTERN =
  '(?<validPeriodStart>\\d{4})/(?<validPeriodEnd>\\d{4})';
export const WIND_GROUP_PATTERN =
  '(?<windDirection>VRB|\\d{3})(?<windSpeed>\\d{2})(G(?<windGust>\\d{2}))?KT';
// Statute-mile visibility can appear as a whole number ("2SM"), a whole number
// plus a fraction ("1 1/2SM"), or a standalone fraction below one mile
// ("1/2SM", "3/4SM", "1/16SM"). Denominators may be two digits (e.g. 16), hence
// `\d{1,2}` on both sides of the fraction.
export const VISIBILITY_GROUP_PATTERN =
  '((((?<visibilityWhole>\\d{1,2})(\\s(?<visibilityFract>\\d{1,2}/\\d{1,2}))?|(?<visibilityFractOnly>\\d{1,2}/\\d{1,2})|(?<visibility>P6))SM|(?<visibilityOk>CAVOK))(\\s(?<visibilityVA>VA))?)|(?<visibilityMeters>\\d{4})';

export const WEATHER_GROUP_QUALIFIER_INTENSITY_PATTERN = '\\-|\\+|VC';
export const WEATHER_GROUP_QUALIFIER_DESCRIPTOR_PATTERN =
  'MI|PR|BC|DR|BL|SH|TS|FZ';

export const WEATHER_GROUP_PHENOMENA_PRECIPITATION_PATTERN =
  'DZ|RA|SN|SG|IC|PL|GR|GS|UP';
export const WEATHER_GROUP_PHENOMENA_OBSCURATION_PATTERN =
  'BR|FG|FU|VA|DU|SA|HZ|PY';
export const WEATHER_GROUP_PHENOMENA_OTHER_PATTERN = 'PO|SQ|FC|SS|DS';

export const NSW_PATTERN = [
  `(?<nswQualifierIntensity>${WEATHER_GROUP_QUALIFIER_INTENSITY_PATTERN})?`,
  `(?<nswQualifierDescriptor>${WEATHER_GROUP_QUALIFIER_DESCRIPTOR_PATTERN})?`,
  `(?<nswPhenomenaPrecipitation>${WEATHER_GROUP_PHENOMENA_PRECIPITATION_PATTERN})?`,
  `(?<nswPhenomenaObscuration>${WEATHER_GROUP_PHENOMENA_OBSCURATION_PATTERN})?`,
  `(?<nswPhenomenaOther>${WEATHER_GROUP_PHENOMENA_OTHER_PATTERN})?`,
].join('');

export const CLOUD_GROUP_PATTERN =
  '((?<skyCoverContraction>VV|FEW|SCT|BKN|OVC)(?<heightsOfClouds>\\d{3})(?<cloudType>CB|TCU)?)|(?<skyClear>SKC|CLR)|(QNH(?<heightOfCloudsINS>\\d{4})INS)';

export const LLWS_GROUP_PATTERN =
  'WS(?<llwsHeight>\\d{3})/(?<llwsDirection>\\d{3})(?<llwsSpeed>\\d{2})KT\\s';

const TAF_PATTERN = [
  `^((?<reportType>${REPORT_TYPE_PATTERN})\\s)?`,
  `(?<stationId>${STATION_ID_PATTERN})\\s`,
  `(?<dateTimeGroup>${DATE_TIME_GROUP_PATTERN})\\s`,
  `(?<validPeriodGroup>${VALID_PERIOD_PATTERN})\\s`,
  `(?<windGroup>${WIND_GROUP_PATTERN})\\s`,
  `(?<visibilityGroup>${VISIBILITY_GROUP_PATTERN})\\s`,
  `(?<nswGroup>(${NSW_PATTERN}\\s)*)`,
  `(?<cloudGroup>((${CLOUD_GROUP_PATTERN})\\s)*)`,
  `(?<llwsGroup>(${LLWS_GROUP_PATTERN})\\s)?`,
  `(?<forecasts>.*)`,
].join('');

const TAF_FORECAST_PATTERN = [
  `((?<fromGroup>FM)(?<dateTime>\\d{6})\\s)?`,
  `((?<tempoGroup>TEMPO)|(?<probGroup>PROB30)|(?<becmgGroup>BECMG)\\s(${VALID_PERIOD_PATTERN})\\s)?`,
  `(?<windGroup>${WIND_GROUP_PATTERN})\\s`,
  `(?<visibilityGroup>${VISIBILITY_GROUP_PATTERN})\\s`,
  `(?<nswGroup>(${NSW_PATTERN}\\s)*)`,
  `(?<cloudGroup>((${CLOUD_GROUP_PATTERN})\\s?)*)`,
  `(?<llwsGroup>(${LLWS_GROUP_PATTERN})\\s?)?`,
].join('');
export const TAF_REGEXP = new RegExp(TAF_PATTERN, 'g');
export interface TafNSW {
  nswQualifierIntensity?: string;
  nswQualifierDescriptor?: string;
  nswPhenomenaPrecipitation?: string;
  nswPhenomenaObscuration?: string;
  nswPhenomenaOther?: string;
}

export interface WeatherConditions {
  validFromString: string;

  validFrom: Dayjs;

  validToString: string;

  validTo: Dayjs;

  wind: string;

  windDirection: string;

  windFormatted: string;
  windSpeed: string;

  windGust: string;

  visibility: number;
  visibilityFormatted: string;

  visibilityWhole?: string;

  visibilityFract?: string;

  visibilityVA?: string;

  weather: string;

  nsw: TafNSW[];
  clouds: TafCloud[];
  ceiling: number;

  llswHeight?: string;

  llswDirection?: string;

  llswSpeed?: string;
  flightCategory: EFlightCategory;
}

export interface TafForecast extends WeatherConditions {
  rawData: string;
  isCurrent?: boolean;
  changeIndicator: 'FM' | 'TEMPO' | 'PROB30' | 'BECMG' | 'CURRENT';
}

export interface TafCloud {
  cloudType?: 'CB' | 'TCU';
  skyCoverContraction: 'VV' | 'FEW' | 'SCT' | 'BKN' | 'OVC';
  ceiling: number;
  formatted: string;
}

export class Taf implements WeatherConditions {
  reportType?: ETafReportType;

  stationId: string;

  issueTime: Dayjs;

  forecasts: TafForecast[];

  validFromString: string;

  validFrom: Dayjs;

  validToString: string;

  validTo: Dayjs;

  wind: string;

  windFormatted: string;

  windDirection: string;

  windSpeed: string;

  windGust: string;

  visibility: number;

  visibilityWhole?: string | undefined;

  visibilityFormatted: string;

  visibilityFract?: string | undefined;

  ceiling: number;

  visibilityVA?: string | undefined;

  weather: string;

  nsw: TafNSW[];

  clouds: TafCloud[];

  llswHeight?: string | undefined;

  llswDirection?: string | undefined;

  llswSpeed?: string | undefined;

  constructor(
    readonly rawData: string,
    readonly timezone: string,
  ) {
    const matches = new RegExp(TAF_PATTERN, 'g').exec(rawData);
    if (!matches?.groups) {
      throw new TafFormatError(rawData);
    }
    const groups = matches.groups as {
      reportType?: ETafReportType;
      stationId: string;
      issueTime: string;
      dateTimeGroup: string;
      validPeriodFrom: string;
      validPeriodTo: string;
      validPeriodGroup: string;
      windGroup: string;
      windFormatted: string;
      windSpeed: string;
      windDirection: string;
      windGust: string;
      visibilityMeters?: string;
      visibilityGroup: string;
      visibilityOk?: 'CAVOK';
      visibilityWhole?: string;
      visibilityFract?: string;
      visibilityFractOnly?: string;
      visibility?: string;
      visibilityVA?: string;
      nswGroup: string;
      cloudGroup: string;
      llwsGroup: string;
      forecasts: string;
      llswHeight?: string;
      llswDirection?: string;
      llswSpeed?: string;
      skyCoverContraction?: string;
      heightsOfClouds?: string;
      cloudType?: string;
      skyClear?: string;
    };
    this.reportType = groups.reportType;
    this.stationId = groups.stationId;
    this.issueTime = this.parseOriginDateTimeGroup(groups.dateTimeGroup);
    this.validFromString = groups.validPeriodFrom;
    this.validToString = groups.validPeriodTo;
    [this.validFrom, this.validTo] = this.parseValidPeriodGroup(
      groups.validPeriodGroup,
    ) as [Dayjs, Dayjs];
    this.windDirection = groups.windDirection;
    this.windSpeed = groups.windSpeed;
    this.windGust = groups.windGust;
    if (this.windDirection === '000' && this.windSpeed === '00') {
      this.windFormatted = `Wind calm.`;
    } else {
      if (this.windDirection === 'VRB') {
        this.windFormatted = `Variable ${Number(this.windSpeed)} knots`;
      } else {
        this.windFormatted = `From ${this.windDirection.padStart(3, '0')}° ${Number(this.windSpeed)} knots`;
      }
      if (this.windGust) {
        this.windFormatted += ` gusting to ${Number(this.windGust)} knots`;
      }
      this.windFormatted += `.`;
    }
    this.wind = groups.windGroup;

    this.visibilityWhole = groups.visibilityWhole;
    this.visibilityFract = groups.visibilityFract ?? groups.visibilityFractOnly;
    this.visibilityVA = groups.visibilityVA;
    if (groups.visibilityMeters) {
      this.visibilityFormatted = `${groups.visibilityMeters} m.`;
    } else if (groups.visibilityOk === 'CAVOK') {
      this.visibilityFormatted = `Ceiling and visibility OK.`;
    } else if (this.visibilityVA) {
      this.visibilityFormatted = `Expected reduction of visibility to ${[this.visibilityWhole, this.visibilityFract].filter(Boolean).join(' and ')} sm by volcanic ash.`;
    } else if (groups.visibility) {
      this.visibilityFormatted = `Unrestricted.`;
    } else {
      this.visibilityFormatted = `${[this.visibilityWhole, this.visibilityFract].filter(Boolean).join(' and ')} sm.`;
    }

    this.nsw = this.parseNSWGroup(groups.nswGroup);
    this.weather = groups.nswGroup.trim();

    this.clouds = this.parseCloudGroup(groups.cloudGroup);
    if (groups.visibilityOk || groups.visibility === 'P6') {
      this.visibility = 6;
    } else if (groups.visibilityMeters) {
      this.visibility = parseInt(groups.visibilityMeters, 10);
    } else {
      const whole = parseInt(groups.visibilityWhole || '0', 10);
      const fract = this.visibilityFract
        ? this.visibilityFract.split('/').map(Number)
        : [0, 1];
      this.visibility = whole + fract[0] / fract[1];
    }
    this.ceiling = Math.min(...this.clouds.map(c => c.ceiling));

    this.flightCategory = this.calculateFlightCategory(
      this.visibility,
      this.ceiling,
    );
    const current: TafForecast = {
      ceiling: this.ceiling,
      rawData: this.rawData.replace(` ${groups.forecasts}`, ''),
      changeIndicator: 'CURRENT',
      validFromString: this.validFromString,
      validFrom: this.validFrom,
      validToString: this.validToString,
      validTo: this.validTo,
      wind: this.wind,
      windFormatted: this.windFormatted,
      windDirection: this.windDirection,
      windSpeed: this.windSpeed,
      windGust: this.windGust,
      visibility: this.visibility,
      visibilityFormatted: this.visibilityFormatted,
      weather: this.weather,
      nsw: this.nsw,
      clouds: this.clouds,
      flightCategory: this.flightCategory,
    };
    this.forecasts = [current, ...this.parseForecasts(groups.forecasts)];
  }

  flightCategory: EFlightCategory;

  private parseOriginDateTimeGroup(originDateTimeGroup: string): Dayjs {
    return dayjs(originDateTimeGroup, 'DDHHmm').tz(this.timezone);
  }

  private parseValidPeriodGroup(
    validPeriodGroup: string,
  ): [Dayjs | null, Dayjs | null] {
    if (!validPeriodGroup) {
      return [null, null];
    }
    const [fromString, toString] = validPeriodGroup.split('/');
    const from = dayjs.utc(fromString, 'DDHH').tz(this.timezone);
    let to = dayjs.utc(toString, 'DDHH').tz(this.timezone);
    if (to.isBefore(from)) {
      to = to.add(1, 'month'); // Adjust to next day if valid to is before valid from
    }
    return [from, to];
  }

  private parseNSWGroup(nswGroup: string) {
    const NSW_REGEXP = new RegExp(NSW_PATTERN, 'g');
    let match: RegExpExecArray | null;
    const nsw: TafNSW[] = [];
    while ((match = NSW_REGEXP.exec(nswGroup)) !== null) {
      if (Object.values(match.groups as TafNSW).some(Boolean)) {
        nsw.push(match.groups as TafNSW);
      } else {
        break;
      }
    }
    return nsw;
  }

  private parseForecasts(forecastsGroup: string) {
    const TAF_FORECAST_REGEXP = new RegExp(TAF_FORECAST_PATTERN, 'g');
    let match: RegExpExecArray | null;
    const forecasts: TafForecast[] = [];
    while ((match = TAF_FORECAST_REGEXP.exec(forecastsGroup)) !== null) {
      if (Object.values(match.groups as Record<string, string>).some(Boolean)) {
        const groups = match.groups as {
          fromGroup?: string;
          tempoGroup?: string;
          probGroup?: string;
          becmgGroup?: string;
          dateTime: string;
          validPeriodStart: string;
          validPeriodEnd: string;
          validPeriodGroup: string;
          windGroup: string;
          windSpeed: string;
          windDirection: string;
          windGust: string;
          visibilityGroup: string;
          visibilityMeters?: string;
          visibilityOk?: 'CAVOK';
          visibilityWhole?: string;
          visibilityFract?: string;
          visibilityFractOnly?: string;
          visibility?: string;
          visibilityVA?: string;
          nswGroup: string;
          cloudGroup: string;
          llwsGroup: string;
          forecasts: string;
          llswHeight?: string;
          llswDirection?: string;
          llswSpeed?: string;
          skyCoverContraction?: string;
          heightsOfClouds?: string;
          cloudType?: string;
          skyClear?: string;
        };
        let validFrom: Dayjs;
        let validFromString: string;
        let validTo: Dayjs;
        let validToString: string;
        if (groups.fromGroup) {
          validFromString = groups.dateTime;
          validFrom = dayjs.utc(groups.dateTime, 'DDHH').tz(this.timezone);
          validToString = this.validToString;
          validTo = this.validTo;
          const prevForecast = forecasts[forecasts.length - 1];
          if (prevForecast?.changeIndicator === 'FM') {
            prevForecast.validTo = validTo.clone();
            prevForecast.validToString = validToString;
          }
        } else {
          validFromString = groups.validPeriodStart;
          validToString = groups.validPeriodEnd;
          [validFrom, validTo] = this.parseValidPeriodGroup(
            groups.validPeriodGroup,
          ) as [Dayjs, Dayjs];
        }
        const clouds = this.parseCloudGroup(groups.cloudGroup);

        const visibilityFract =
          groups.visibilityFract ?? groups.visibilityFractOnly;

        let visibility: number;

        if (groups.visibilityMeters) {
          visibility = parseInt(groups.visibilityMeters);
        } else if (groups.visibilityOk || groups.visibility === 'P6') {
          visibility = 6;
        } else {
          const whole = parseInt(groups.visibilityWhole || '0', 10);
          const fract = visibilityFract
            ? visibilityFract.split('/').map(Number)
            : [0, 1];
          visibility = whole + fract[0] / fract[1];
        }
        const ceiling = Math.min(...clouds.map(c => c.ceiling));
        let windFormatted = '';
        if (groups.windDirection === '000' && groups.windSpeed === '00') {
          windFormatted = `Wind calm.`;
        } else {
          if (groups.windDirection === 'VRB') {
            windFormatted = `Variable ${Number(groups.windSpeed)} knots`;
          } else {
            windFormatted = `From ${groups.windDirection.padStart(3, '0')}° ${Number(groups.windSpeed)} knots`;
          }
          if (groups.windGust) {
            windFormatted += ` gusting to ${Number(groups.windGust)} knots`;
          }
          windFormatted += `.`;
        }
        let visibilityFormatted = '';
        if (groups.visibilityOk === 'CAVOK') {
          visibilityFormatted = `Ceiling and visibility OK.`;
        } else if (groups.visibilityVA) {
          visibilityFormatted = `Expected reduction of visibility to ${[groups.visibilityWhole, visibilityFract].filter(Boolean).join(' and ')} sm by volcanic ash.`;
        } else if (groups.visibility) {
          visibilityFormatted = `Unrestricted.`;
        } else {
          visibilityFormatted = `${[groups.visibilityWhole, visibilityFract].filter(Boolean).join(' and ')} sm.`;
        }

        // The change groups are mutually exclusive in the grammar; FM is the
        // default when none of the explicit change keywords matched.
        let changeIndicator: TafForecast['changeIndicator'] = 'FM';
        if (groups.tempoGroup) {
          changeIndicator = 'TEMPO';
        } else if (groups.probGroup) {
          changeIndicator = 'PROB30';
        } else if (groups.becmgGroup) {
          changeIndicator = 'BECMG';
        }

        forecasts.push({
          rawData: match[0],
          changeIndicator,
          validFromString,
          validToString,
          validFrom,
          validTo,
          wind: groups.windGroup,
          windFormatted,
          windDirection: groups.windDirection,
          windSpeed: groups.windSpeed,
          windGust: groups.windGust,
          visibility,
          visibilityWhole: groups.visibilityWhole,
          visibilityFract,
          visibilityVA: groups.visibilityVA,
          visibilityFormatted,
          weather: groups.nswGroup,
          nsw: this.parseNSWGroup(groups.nswGroup),
          clouds,
          ceiling,
          llswHeight: groups.llswHeight,
          llswDirection: groups.llswDirection,
          llswSpeed: groups.llswSpeed,
          flightCategory: this.calculateFlightCategory(visibility, ceiling),
        });
      } else {
        break;
      }
    }
    return forecasts;
  }

  parseCloudGroup(cloudGroup: string): TafCloud[] {
    const CLOUDS_REGEXP = new RegExp(CLOUD_GROUP_PATTERN, 'g');
    let match: RegExpExecArray | null;
    const clouds: TafCloud[] = [];
    const ceilingFormatter = new Intl.NumberFormat('en-EN', {
      style: 'unit',
      unit: 'foot',
    });
    while ((match = CLOUDS_REGEXP.exec(cloudGroup)) !== null) {
      const groups = match.groups as {
        skyCoverContraction: 'VV' | 'FEW' | 'SCT' | 'BKN' | 'OVC';
        heightsOfClouds: string;
        cloudType?: 'CB' | 'TCU';
        skyClear?: 'SKC' | 'CLR';
        heightOfCloudsINS?: string;
      };
      // The `QNH<dddd>INS` altimeter setting (e.g. "QNH2992INS") shares this
      // pattern so the parser can consume it inline, but it is a pressure
      // reading, not a cloud layer. Skip it — otherwise it is pushed as a
      // phantom layer with a NaN ceiling, which poisons `Math.min(...ceilings)`
      // and forces the whole report to LIFR (TSMED-729).
      if (
        groups.heightOfCloudsINS &&
        !groups.skyCoverContraction &&
        !groups.skyClear
      ) {
        continue;
      }
      if (Object.values(groups).some(Boolean)) {
        let ceiling = 0;
        let formatted = '';
        if (groups.skyClear === 'SKC') {
          ceiling = 12000;
          formatted = 'no layers are present';
        } else if (groups.skyClear === 'CLR') {
          ceiling = 12000;
          formatted = 'no layers are detected at or below 12,000 ft AGL';
        } else {
          ceiling = Number(groups.heightsOfClouds) * 100;
          const cloudTypeString =
            (groups.cloudType === 'TCU' && ' of towering cumulus') ||
            (groups.cloudType === 'CB' && ' of cumulonimbus') ||
            '';
          if (groups.skyCoverContraction === 'FEW') {
            formatted = `few${cloudTypeString} at ${ceilingFormatter.format(ceiling)} AGL`;
          } else if (groups.skyCoverContraction === 'SCT') {
            formatted = `scattered layer${cloudTypeString} at ${ceilingFormatter.format(ceiling)} AGL`;
          } else if (groups.skyCoverContraction === 'BKN') {
            formatted = `broken layer (ceiling)${cloudTypeString} at ${ceilingFormatter.format(ceiling)} AGL`;
          } else if (groups.skyCoverContraction === 'OVC') {
            formatted = `overcast layer (ceiling)${cloudTypeString} at ${ceilingFormatter.format(ceiling)} AGL`;
          } else if (groups.skyCoverContraction === 'VV') {
            formatted = `indefinite ceiling with a vertical visibility of ${ceilingFormatter.format(ceiling)} AGL`;
          }
        }

        clouds.push({
          ceiling,
          skyCoverContraction: groups.skyCoverContraction,
          cloudType: groups.cloudType,
          formatted,
        });
      } else {
        break;
      }
    }
    return clouds;
  }

  calculateFlightCategory(
    visibility: number,
    ceiling: number,
  ): EFlightCategory {
    switch (true) {
      case visibility >= 5 && ceiling >= 3000:
        return EFlightCategory.VFR;
      case visibility >= 3 && ceiling >= 1000:
        return EFlightCategory.MVFR;
      case visibility >= 1 && ceiling >= 500:
        return EFlightCategory.IFR;
      default:
        return EFlightCategory.LIFR;
    }
  }
}
